import Ajv2020 from 'ajv/dist/2020.js';
import addFormats from 'ajv-formats';
import { charterSchema, toolsSchema, integrationMetadataSchema } from './loaders.js';
import type { CharterFrontmatter } from '../types/charter.js';
import type { ToolsFrontmatter } from '../types/tools.js';
import type { IntegrationMetadata, RuntimeScopeName } from '../types/integration-metadata.js';
import { isRuntimeScopeSupported } from '../types/integration-metadata.js';

const ajv = new Ajv2020({ allErrors: true, strict: false });
addFormats(ajv);

const compiledCharter = ajv.compile<CharterFrontmatter>(charterSchema);
const compiledTools = ajv.compile<ToolsFrontmatter>(toolsSchema);
const compiledIntegrationMetadata = ajv.compile<IntegrationMetadata>(integrationMetadataSchema);

export interface SchemaValidationResult<T> {
  valid: boolean;
  data?: T;
  errors: SchemaError[];
}

export interface SchemaError {
  path: string;
  message: string;
}

function formatErrors(errors: typeof compiledCharter.errors): SchemaError[] {
  if (!errors) return [];
  return errors.map((e) => ({
    path: e.instancePath || '/',
    message: e.message ?? 'Unknown validation error',
  }));
}

export function validateCharterFrontmatter(data: unknown): SchemaValidationResult<CharterFrontmatter> {
  const valid = compiledCharter(data);
  return {
    valid,
    data: valid ? (data as CharterFrontmatter) : undefined,
    errors: formatErrors(compiledCharter.errors),
  };
}

export function validateToolsFrontmatter(data: unknown): SchemaValidationResult<ToolsFrontmatter> {
  const valid = compiledTools(data);
  return {
    valid,
    data: valid ? (data as ToolsFrontmatter) : undefined,
    errors: formatErrors(compiledTools.errors),
  };
}

export function validateIntegrationMetadata(data: unknown): SchemaValidationResult<IntegrationMetadata> {
  const valid = compiledIntegrationMetadata(data);
  return {
    valid,
    data: valid ? (data as IntegrationMetadata) : undefined,
    errors: formatErrors(compiledIntegrationMetadata.errors),
  };
}

/**
 * Throws if the integration's metadata does not support the given
 * runtime scope. Use at the API edge from POST /organizations/:id/
 * integrations, POST /teams/:id/integrations, and POST /agents/:id/
 * integrations to gate enrolment writes.
 *
 * Definitions without a `runtime_scopes` field are treated as agent-only
 * for backwards-compat (see isRuntimeScopeSupported).
 */
export function assertRuntimeScopeSupported(
  metadata: IntegrationMetadata | null | undefined,
  scope: RuntimeScopeName,
  definitionId: string,
): void {
  if (!isRuntimeScopeSupported(metadata, scope)) {
    throw new IntegrationScopeNotSupportedError(definitionId, scope);
  }
}

export class IntegrationScopeNotSupportedError extends Error {
  readonly status = 400;
  readonly definitionId: string;
  readonly scope: RuntimeScopeName;
  constructor(definitionId: string, scope: RuntimeScopeName) {
    super(`integration "${definitionId}" does not support ${scope}-scoped installs`);
    this.name = 'IntegrationScopeNotSupportedError';
    this.definitionId = definitionId;
    this.scope = scope;
  }
}
